import JamSpinner from '@/src/components/common/JamSpinner/JamSpinner';
import JamConstants from '@/src/constants/shared';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { ChallengeLearningType } from '@/src/types/Challenge';
import { SolveChallengeButtonKey } from '@/src/types/JamChallengeDetails';
import { JamEventStatus } from '@/src/types/JamCommon';
import { AccountCredentials } from '@/src/types/LabModels';
import { delay } from '@/src/utils/delay';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { disableRestartChallengeRegex } from '@/src/utils/myjam.utils';
import { getTimelineFormattedDate } from '@/src/utils/time.utils';
import { Box, Button, Container, Icon, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AwsCliModal from './Modals/AwsCLI/AwsCLIModal';
import RestartChallengeModal from './Modals/RestartChallengeModal';
import styles from './SolveHeader.module.scss';
import { config } from '@/src/config/app-config';
import { useParams } from 'react-router-dom';

const JamChallengeSolve: React.FC = () => {
  const { t } = useTranslation();
  const { eventName, event } = useJamEventDetails();
  const {
    startJamChallenge,
    loadJamChallengeProgressData,
    matchedChallengeProgress,
    progressMatchesSelectedChallenge,
    showChallengeStatusProgress,
    trackSolveWithButtonClick,
    loadTeamKeyPair,
    awsAccount,
    isStartingChallange,
    selectedJamChallenge,
  } = useJamChallengeDetails();
  const { clearFlashbars } = useFlashbars();
  const [enableModal, setModal] = useState(false);
  const [enableChallengeRestartModal, setEnableChallengeRestartModal] = useState(false);
  const { awsCLI, restartChallenge, sshKeyPair, startChallenge } =
    i18nKeys.myJams.challenges.details.solveUsing.buttons;

  const isLocked = !!selectedJamChallenge?.locked;
  const { 1: jamChallengeId }: { 0: string; 1: string } = useParams();

  useEffect(() => {
    clearFlashbars();
    if (!!jamChallengeId && (event?.eventStatus === JamEventStatus.ONGOING || event?.ended)) {
      void loadJamChallengeProgressData({ id: eventName, challengeId: jamChallengeId });
    }
  }, [jamChallengeId]);

  const handleProgressChallenge = () => {
    // Never act on a locked challenge.
    if (isLocked) {
      return;
    }
    // Restart path: only when progress matches the selected challenge and it has started.
    // The stale/mismatched-progress guard applies to RESTART only
    if (progressMatchesSelectedChallenge && matchedChallengeProgress?.started) {
      setEnableChallengeRestartModal(!enableChallengeRestartModal);
    } else {
      void startJamChallenge({ id: eventName, challengeId: jamChallengeId });
    }
  };

  const openSignoutConsole = async () => {
    if (JamConstants.FORCED_SIGNOUT_DELAY_MS > 0) {
      window.open(JamConstants.CONSOLE_SIGNOUT_URL, JamConstants.CONSOLE_WINDOW_NAME);
      await delay(JamConstants.FORCED_SIGNOUT_DELAY_MS);
    }
  };

  const openConsole = async (consoleUrl: string | undefined) => {
    window.open(consoleUrl, JamConstants.CONSOLE_WINDOW_NAME);
  };

  const handleOpenAWSConsole = async (consoleUrl: string | undefined) => {
    await openSignoutConsole();
    openConsole(consoleUrl);
    void trackSolveWithButtonClick(eventName, jamChallengeId, SolveChallengeButtonKey.AWS_CONSOLE);
  };

  const handleSSHKeyPair = () => {
    void loadTeamKeyPair(eventName, jamChallengeId);
  };

  const handleAwsCLI = () => {
    setModal(!enableModal);
    void trackSolveWithButtonClick(eventName, jamChallengeId, SolveChallengeButtonKey.AWS_CLI);
  };

  // No keys are sent when the event owner disabled CLI access for this challenge.
  const hasCliCredentials = !!(awsAccount?.accessKey && awsAccount?.secretKey && awsAccount?.sessionToken);

  const credentials = useMemo<AccountCredentials | undefined>(() => {
    if (!awsAccount) return;
    const creds = new AccountCredentials();
    creds.accessKey = awsAccount.accessKey;
    creds.secretKey = awsAccount.secretKey;
    creds.sessionToken = awsAccount.sessionToken;
    return creds;
  }, [awsAccount]);

  const hideConsoleAccess = useMemo(
    () => config.ff.disableGameUIAwsConsoleForChallenges?.includes(jamChallengeId),

    [config, jamChallengeId]
  );

  if (isStartingChallange) {
    return <JamSpinner />;
  }

  // Ordered by precedence. A timed-out lab also has no account, so restartRequired keeps it out of
  // "provisioning". labsAvailable only blocks when explicitly false (undefined = unknown).
  const getRestartDisabledReason = (): string | undefined => {
    if (showChallengeStatusProgress && !selectedJamChallenge?.restartRequired) {
      return t(restartChallenge.disabledReasons.provisioning);
    }
    if ((matchedChallengeProgress?.remainingRestarts ?? 0) < 1) {
      return t(restartChallenge.disabledReasons.noRestartsRemaining);
    }
    if (selectedJamChallenge?.labsAvailable === false) {
      return t(restartChallenge.disabledReasons.noLabsAvailable);
    }
    return undefined;
  };

  const renderActionButton = () => {
    if (disableRestartChallengeRegex.test(location.pathname) || isLocked) {
      return null;
    }

    if (!matchedChallengeProgress?.started) {
      return (
        <Button id="startChallengeButton" data-testid="startChallengeButton" onClick={handleProgressChallenge}>
          <Icon name="caret-right-filled" />
          <Box margin={{ left: 'xs' }} variant="span" color="text-status-info">
            {t(startChallenge.title)}
          </Box>
        </Button>
      );
    }

    if (
      matchedChallengeProgress?.hasAwsResources ||
      matchedChallengeProgress?.hasExternalResources ||
      matchedChallengeProgress?.awaitingLabAssignment
    ) {
      const restartDisabledReason = getRestartDisabledReason();
      return (
        <Button
          id="restartChallengeButton"
          data-testid="restartChallengeButton"
          onClick={handleProgressChallenge}
          disabled={!!restartDisabledReason}
          disabledReason={restartDisabledReason}
        >
          <Icon name="refresh" />
          <Box margin={{ left: 'xs' }} variant="span" color="text-status-info">
            {t(restartChallenge.title)}
          </Box>
        </Button>
      );
    }
  };

  const renderConsoleButtons = () => {
    if (hideConsoleAccess || !awsAccount) {
      return null;
    }

    return (
      <SpaceBetween size="m" direction="horizontal">
        <Box variant="p">
          <Box variant="span" color="text-status-info">
            <Icon name="status-info" ariaLabel={t(i18nKeys.general.info)} />
          </Box>
          <Box variant="span" fontWeight="bold" margin={{ left: 'xs' }}>
            {t(i18nKeys.myJams.challenges.details.solveUsing.title)}:
          </Box>
        </Box>
        {awsAccount?.url && (
          <Button
            variant="primary"
            rel="noopener noreferrer"
            onClick={() => handleOpenAWSConsole(awsAccount?.url)}
            ariaLabel={t(i18nKeys.myJams.challenges.details.solveUsing.buttons.openAWSConsoleAriaLabel)}
            data-testid="open-aws-console-btn"
          >
            {t(i18nKeys.myJams.challenges.details.solveUsing.buttons.openAWSConsole)}
          </Button>
        )}
        {selectedJamChallenge?.learningType !== ChallengeLearningType.INDIVIDUAL && hasCliCredentials && (
          <Button onClick={handleAwsCLI} data-testid="aws-cli-btn">
            <Box variant="span" color="text-status-info">
              {t(awsCLI)}
            </Box>
          </Button>
        )}
      </SpaceBetween>
    );
  };

  const renderSshKeyPairButton = () => {
    if (selectedJamChallenge?.learningType === ChallengeLearningType.INDIVIDUAL) {
      return null;
    }

    if (isLocked) {
      return null;
    }

    if (matchedChallengeProgress?.completed) {
      return null;
    }

    if (!matchedChallengeProgress?.started) {
      return null;
    }

    if (!selectedJamChallenge?.sshKeyPairRequired) {
      return null;
    }

    return (
      <>
        <Box className="text-grid" />
        <Button onClick={handleSSHKeyPair} data-testid="ssh-keypair-btn">
          <Icon name="download" />
          <Box margin={{ left: 'xs' }} variant="span" color="text-status-info">
            {t(sshKeyPair)}
          </Box>
        </Button>
      </>
    );
  };

  const renderNotStartedBanner = () => {
    if (event?.eventStatus !== JamEventStatus.NOT_STARTED) {
      return null;
    }

    return (
      <Box className="light-violet">
        {t(i18nKeys.events.eventDetails.timeline.scheduledFor)}
        {`${getTimelineFormattedDate(new Date(event?.eventStartDate || ''))}`}
      </Box>
    );
  };

  const renderEndedBanner = () => {
    if (!(event?.eventStatus === JamEventStatus.ENDED && isLocked)) {
      return null;
    }

    return <Box className="light-violet">{t(i18nKeys.myJams.challenges.header.timeExpired)}</Box>;
  };

  const renderContainer = () => {
    const consoleButtons = renderConsoleButtons();
    const sshKeyPairButton = renderSshKeyPairButton();
    const notStartedBanner = renderNotStartedBanner();
    const actionButton = renderActionButton();
    const endedBanner = renderEndedBanner();

    if (!consoleButtons && !sshKeyPairButton && !notStartedBanner && !actionButton && !endedBanner) {
      return null;
    }

    return (
      <Container variant="default" className="dark-bg">
        <div className={styles.buttonContainer}>
          <div>{consoleButtons}</div>
          <Box float="right">
            <SpaceBetween size="m" direction="horizontal">
              {sshKeyPairButton}
              {notStartedBanner}
              {actionButton}
              {endedBanner}
            </SpaceBetween>
          </Box>
        </div>
      </Container>
    );
  };

  return (
    <>
      {renderContainer()}
      {enableModal && hasCliCredentials && credentials && (
        <AwsCliModal value={enableModal} setValue={setModal} awsAccount={credentials} />
      )}
      {enableChallengeRestartModal && (
        <RestartChallengeModal value={enableChallengeRestartModal} setValue={setEnableChallengeRestartModal} />
      )}
    </>
  );
};

export default JamChallengeSolve;
